import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateNotice,
	useNoticeAdminById,
	useUpdateNotice,
} from '@/api/queries/notices-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	NotificationFormContent,
	type NotificationFormData,
} from '@/components/administration'
import { AdminCardPage } from '@/components/commons'
import { ROUTES } from '@/constants/routes'
import { getApiErrorMessage } from '@/utils/api-error'

/** Criação e edição de avisos (edição: `?id=<aviso>`), no lugar do modal que ficou pequeno demais. */
export const NotificationFormPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const editingId = searchParams.get('id')

	const { data: orgData } = useOrganization()
	const { data: notice, isLoading, isError } = useNoticeAdminById(editingId)
	const createMutation = useCreateNotice()
	const updateMutation = useUpdateNotice()

	const goBack = () => navigate(ROUTES.ADMINISTRATION.NOTIFICATIONS)

	const handleSave = async (
		data: NotificationFormData,
		onProgress: (percent: number) => void,
	) => {
		try {
			if (editingId) {
				await updateMutation.mutateAsync({ id: editingId, data, onProgress })
				toast.success(
					t('administration.notifications.editSuccess', { title: data.title }),
				)
			} else {
				await createMutation.mutateAsync({ data, onProgress })
				toast.success(t('administration.notificationsForm.saveSuccess'))
			}
			goBack()
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<AdminCardPage
			icon={<Bell className="size-5" />}
			title={t(
				editingId
					? 'administration.notifications.editNotification'
					: 'administration.notifications.createNew',
			)}
			subtitle={t('administration.notifications.subtitle')}
			onBack={goBack}
		>
			{editingId && isLoading ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			) : editingId && (isError || !notice) ? (
				<p className="py-16 text-center text-destructive text-sm">
					{t('administration.notifications.loadError')}
				</p>
			) : (
				<div className="mx-auto w-full max-w-3xl">
					<NotificationFormContent
						key={editingId ?? 'new'}
						mode={editingId ? 'edit' : 'create'}
						initial={notice}
						branches={orgData ?? []}
						onSave={handleSave}
						onClose={goBack}
					/>
				</div>
			)}
		</AdminCardPage>
	)
}
